import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { AIMateAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { RemixContainer } from '../remix';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { RemixOptionSelectionParameters } from '../remix/remix-overlay';

import { EditorRemixContextProvider, useEditorRemixContext } from './context';
import { LocationPickerPopup } from './LocationPickerPopup';
import type {
	DynamicUiSource,
	EditorApiWithCorePlugin,
	RemixContentComponentFullProps,
	RemixHistoryEntryForUpdate,
} from './types';
import { useOnComplete as useOnCompleteNew } from './useOnComplete';
import { useUpdateRemix, type UpdateRemix } from './useUpdateRemix';
import { filterRemixFragmentNodes, useRemixToAIFC } from './utils';

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useOnCompleteOld() {}

const useOnComplete = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useOnCompleteNew,
	useOnCompleteOld,
);

type RemixUpdateSubmissionHistoryEntry = Pick<
	RemixHistoryEntryForUpdate,
	'conversationId' | 'fieldId' | 'url'
>;

export const getUpdateRemixSubmissionDecision = (
	historyEntryForUpdate: RemixUpdateSubmissionHistoryEntry | undefined,
	dynamicUiSubtypeForUpdate: string | undefined,
	submittedUpdateKey: string | undefined,
): { shouldSubmit: boolean; updateKey?: string } => {
	if (!historyEntryForUpdate || !dynamicUiSubtypeForUpdate) {
		return { shouldSubmit: false };
	}

	const updateKey = [
		historyEntryForUpdate.conversationId,
		historyEntryForUpdate.url ?? historyEntryForUpdate.fieldId ?? '',
		dynamicUiSubtypeForUpdate,
	].join(':');

	return {
		shouldSubmit: submittedUpdateKey !== updateKey,
		updateKey,
	};
};

/**
 * editor has two modes, editing / viewing
 * - for editing mode, the thinking bar is rendered by the editor plugin
 * - for viewing mode, the thinking bar is rendered by the malleable-ui components
 * so the flow is different in two modes, specifically the onCompleteAnimationFinished callback
 */
const RemixContentComponentInner = <
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
>({
	handleModalClose,
	selectedContext,
	editorApi,
	isComplete: pluginIsComplete,
	isThinking,
	onStartThinking,
	onDismissThinkingUi,
	onStopThinking,
	onRegisterAbortRequest,
	finishRemixThinking,
	remixModalMetadata,
	remixSource,
	productIntegration,
	productConfig,
	hiddenOptions,
}: RemixContentComponentFullProps<TEditorApi>) => {
	const {
		isViewMode,
		setDynamicUiType,
		setDynamicUiSubtype,
		setDynamicUiSource,
		setInteractionSource,
		setAspectRatio,
		showLocationPicker,
		setThinkingComplete,
		clearThinkingDecoration,
		isUpdate,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		onRemixThinkingCompleteAnimationFinished,
		resetContext,
	} = useEditorRemixContext();
	const isRemixCancelled = useRef(false);
	const experienceTracker = useAIMateExperienceTracker();
	const openRemixPreviewRef = useRef<() => void>(() => {});
	const selectedSubTypeRef = useRef<string | undefined>(undefined);
	const hasAutoSubmittedRef = useRef(false);
	const remixModalMetadataRef = useRef(remixModalMetadata);
	remixModalMetadataRef.current = remixModalMetadata;
	const isThinkingRef = useRef(isThinking);
	isThinkingRef.current = isThinking;
	const updateRemixRef = useRef<UpdateRemix | undefined>(undefined);

	const { dynamicUiType } = useStagingAreaState();

	// remove legacy local state when cleanup platform_editor_ai_remix_in_editor_loading
	const [isComplete, setIsComplete] = useState(false);
	const resetCompletion = useCallback(() => {
		setIsComplete(false);
	}, []);

	const openPreviewAndTrack = useCallback(() => {
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		experienceTracker.start({
			name: ExperienceName.REMIX_RENDERS_IN_MODAL,
			id: ExperienceName.REMIX_RENDERS_IN_MODAL,
			attributes: {
				product: productConfig.analyticsProductName ?? productConfig.productKey,
				remixType: dynamicUiType,
				remixSubtype: selectedSubTypeRef.current,
				entryPoint: 'editor',
			},
		});
		openRemixPreviewRef.current();
		// stop thinking when showing the preview modal, otherwise,
		// the thinking bar will flash when the preview modal close
		if (fg('platform_editor_ai_remix_in_editor_loading')) {
			if (isViewMode && !fg('cc-maui-fix-live-doc-previews')) {
				onDismissThinkingUi?.({ reopenModal: false });
			} else {
				onStopThinking({ reopenModal: false });
			}
		}
	}, [
		dynamicUiType,
		experienceTracker,
		isViewMode,
		onDismissThinkingUi,
		onStopThinking,
		productConfig.analyticsProductName,
		productConfig.productKey,
	]);

	// when updating in editing mode, will call this callback when the thinking bar is complete
	// when in viewing mode, we need to open the preview modal when the thinking bar is complete
	const onCompleteAnimationFinished = useCallback(() => {
		if (!isViewMode && fg('platform_editor_ai_remix_toolbar')) {
			onRemixThinkingCompleteAnimationFinished?.();
		} else {
			openPreviewAndTrack();
		}
	}, [openPreviewAndTrack, onRemixThinkingCompleteAnimationFinished, isViewMode]);

	const onActionReady = useCallback(() => {
		if (!isRemixCancelled.current) {
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED);
			if (fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true) {
				// When we use the "showProgressBar"
				if (fg('platform_editor_maui_jira_updates')) {
					// Defer opening the preview until the progress bar fills to 100%.
					if (fg('platform_editor_ai_remix_in_editor_loading')) {
						setThinkingComplete?.(true);
					} else {
						setIsComplete(true);
					}
				} else {
					openPreviewAndTrack();
				}
			}
		}
	}, [openPreviewAndTrack, setThinkingComplete, isViewMode]);

	const onStagingAIFCFlowStarted = useCallback(() => {
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		isRemixCancelled.current = true;
		clearThinkingDecoration?.();
		finishRemixThinking?.();
		handleModalClose();
	}, [clearThinkingDecoration, experienceTracker, finishRemixThinking, handleModalClose]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview,
	} = useRemixToAIFC({
		config: productConfig,
		onActionReady,
		productIntegration,
		onStagingAIFCFlowStarted,
	});
	openRemixPreviewRef.current = openRemixPreview;

	// Register stopRemixing so that cancelRemixThinking (called from the decoration or
	// RemixContent.tsx's onStopThinking for the toolbar ON path) can abort the in-flight
	// AI request and trigger the paired aiInteraction initiated + dismissed analytics.
	useEffect(() => {
		return onRegisterAbortRequest?.(stopRemixing);
	}, [onRegisterAbortRequest, stopRemixing]);

	const { updateRemix } = useUpdateRemix();
	updateRemixRef.current = updateRemix;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			if (isRemixCancelled.current) {
				return;
			}
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
		};
	}, []);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				// when start/cancel, reset the context
				resetContext();
			}

			if (active) {
				isRemixCancelled.current = false;
				// Clear stale completion state from any previous flow.
				if (!fg('platform_editor_ai_remix_in_editor_loading')) {
					resetCompletion();
				}

				// When isThinking is already true (e.g. pre-supplied userPrompt flow from
				// startRemixThinking), skip calling onStartThinking again — it would wipe
				// remixModalMetadata from plugin state by dispatching a new startRemixThinking
				// without the metadata. Use ref to avoid stale closure.
				if (!isThinkingRef.current) {
					onStartThinking(
						fg('platform_editor_ai_remix_in_editor_loading')
							? {
									contentType: productIntegration.contentType,
								}
							: undefined,
					);
				}
				return;
			}

			isRemixCancelled.current = true;
			experienceTracker.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User cancelled remix thinking',
				attributes: { reason: 'User cancelled remix thinking' },
			});
			remixGeneratePreviewExperience.abort({
				metadata: { reason: 'User cancelled remix thinking' },
			});

			if (
				expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
			) {
				const userPrompt = remixModalMetadataRef.current?.userPrompt;
				const onCancel = remixModalMetadataRef.current?.onCancel;
				if (onCancel && userPrompt) {
					// Abort the AI request so send-message-action.ts fires the paired
					// aiInteraction initiated + dismissed events automatically via AbortError.
					stopRemixing();
					onStopThinking({ reopenModal: false });
					onCancel();
					return;
				}
			}

			stopRemixing();
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'cancelledRemixThinking',
					originalRemixSource: remixSource,
					product: productConfig.analyticsProductName ?? productConfig.productKey,
				},
			});
			if (fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true) {
				onStopThinking({ reopenModal: true });
			}
		},
		[
			isViewMode,
			experienceTracker,
			onStartThinking,
			onStopThinking,
			productConfig.analyticsProductName,
			productConfig.productKey,
			productIntegration.contentType,
			remixSource,
			resetCompletion,
			stopRemixing,
			resetContext,
		],
	);

	const onRemixFlowActiveChangeRef = useRef(onRemixFlowActiveChange);
	useEffect(() => {
		onRemixFlowActiveChangeRef.current = onRemixFlowActiveChange;
	}, [onRemixFlowActiveChange]);

	// When thinking stops without completing, treat it as cancellation.
	const previousIsThinking = useRef(false);
	useEffect(() => {
		const isImageGenFlow =
			expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true) &&
			!!remixModalMetadataRef.current?.userPrompt;
		const shouldFire =
			(!isImageGenFlow && fg('platform_editor_ai_remix_in_editor_loading')) ||
			(isImageGenFlow && fg('platform_editor_ai_remix_toolbar'));
		if (
			!isViewMode &&
			previousIsThinking.current &&
			!isThinking &&
			pluginIsComplete !== true &&
			showLocationPicker !== true &&
			!isRemixCancelled.current &&
			shouldFire
		) {
			onRemixFlowActiveChangeRef.current(false);
		}
		previousIsThinking.current = isThinking;
	}, [isThinking, pluginIsComplete, showLocationPicker, isViewMode]);

	// Dismiss the thinking bar when the user stops the remix from the Rovo side chat.
	useEffect(() => {
		if (isRemixAborted && !isRemixCancelled.current) {
			experienceTracker.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User stopped from Rovo side chat',
			});
			onStopThinking();
		}
	}, [isRemixAborted, onStopThinking, experienceTracker]);

	// Fail experience and clean up thinking UI when Rovo encounters an error.
	const previousIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!previousIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error(errorMessage),
				attributes: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			remixGeneratePreviewExperience.failure({
				metadata: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			onStopThinking({ reopenModal: true });
		}
		previousIsRemixError.current = isRemixError;
	}, [experienceTracker, isRemixError, onStopThinking, remixStoreError]);

	// When an error occurs, close the ephemeral preview if open, dismiss the
	// remix UI entirely, and open the sidebar so the user can see the error.
	useEffect(() => {
		if (!isRemixCancelled.current && isRemixError) {
			if (
				productIntegration.isRemixPreviewVisible &&
				(fg('platform_editor_ai_remix_in_editor_loading') ? isViewMode : true)
			) {
				closeRemixPreview();
			}

			// Use handleModalClose (not onStopThinking) to fully unmount the component
			// and clear isModalOpen. The first error effect already sets reopenModal:true
			// via onStopThinking; using it here alone would leave the progress bar visible.
			handleModalClose();
			productIntegration.toggleConversationAssistant?.('sidebar');
		}
	}, [closeRemixPreview, handleModalClose, isRemixError, productIntegration, isViewMode]);

	const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
		const localIds = selectedContext?.localIds ?? [];
		const startLocalId = localIds[0] ?? '';
		const endLocalId = localIds[localIds.length - 1] ?? '';
		const fragmentAdf = filterRemixFragmentNodes(selectedContext?.selectionFragment ?? null);

		let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
		if (editorApi) {
			fullAdf = await new Promise((resolve) => {
				try {
					editorApi.core?.actions?.requestDocument?.(
						(doc?: JSONDocNode) => {
							resolve((doc as DocNode | undefined) ?? { type: 'doc', version: 1, content: [] });
						},
						{ alwaysFire: true },
					);
				} catch {
					resolve({ type: 'doc', version: 1, content: [] });
				}
			});
		}

		// When the entire page is selected (e.g. cmd+a), selectionFragment is null
		// because ProseMirror's AllSelection does not produce a fragment. Fall back
		// to the full document content so Remix still receives the source it needs.
		const resolvedFragmentAdf = fragmentAdf ?? fullAdf.content;

		return {
			contentType: productIntegration.contentType,
			startLocalId,
			endLocalId,
			startIndex: selectedContext?.startIndex ?? 0,
			endIndex: selectedContext?.endIndex ?? -1,
			fragmentAdf: resolvedFragmentAdf,
			fullAdf,
		};
	}, [editorApi, productIntegration.contentType, selectedContext]);

	const onOptionSelect = useCallback(
		async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setDynamicUiType(type);
				setDynamicUiSubtype(subType);
				setInteractionSource(remixModalMetadata?.interactionSource);
				setAspectRatio(remixModalMetadata?.aspectRatio);
			}
			selectedSubTypeRef.current = subType;
			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { remixType: type, remixSubtype: subType },
			});

			remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: productConfig.analyticsProductName ?? productConfig.productKey,
				interactionSource: remixModalMetadata?.interactionSource,
				remixType: type,
				remixSubtype: subType,
				entryPoint: 'editor',
			});

			let dynamicUiSource = await constructDynamicUiSource();
			// When the AI Image Generation media-insert tab is the entry point there is
			// no editor selection - the user's typed prompt IS the "highlighted text".
			// Mirror the shape the existing "click Image in Visual aid accordion" flow
			// produces (a doc-ADF with the source content) so the backend image-generation
			// path receives the prompt via dynamicUiSource.fragmentAdf / fullAdf the same
			// way it normally receives selected text.
			if (type === 'image' && userPrompt) {
				const promptDoc: DocNode = {
					type: 'doc',
					version: 1,
					content: [
						{
							type: 'paragraph',
							content: [{ type: 'text', text: userPrompt }],
						},
					],
				};
				dynamicUiSource = {
					contentType: dynamicUiSource?.contentType ?? productIntegration.contentType,
					startLocalId: dynamicUiSource?.startLocalId ?? '',
					endLocalId: dynamicUiSource?.endLocalId ?? '',
					startIndex: dynamicUiSource?.startIndex ?? 0,
					endIndex: dynamicUiSource?.endIndex ?? -1,
					fragmentAdf: promptDoc.content,
					fullAdf: promptDoc,
				};
			}
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setDynamicUiSource(dynamicUiSource);
			}
			void sendAIFCPrompt({
				userPrompt,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource: remixModalMetadata?.interactionSource,
				aspectRatio: remixModalMetadata?.aspectRatio,
				aiFeatureName: remixModalMetadata?.aiFeatureName,
			});
		},
		[
			setDynamicUiType,
			setDynamicUiSubtype,
			setInteractionSource,
			setAspectRatio,
			constructDynamicUiSource,
			experienceTracker,
			productConfig.analyticsProductName,
			productConfig.productKey,
			productIntegration.contentType,
			remixModalMetadata,
			sendAIFCPrompt,
			setDynamicUiSource,
		],
	);

	// When a userPrompt is pre-supplied in remixModalMetadata (e.g. from the AI Image Generation
	// submit flow), skip the option-picker modal UI and go straight to processing.
	// The ref guard prevents double-firing if the component re-renders.
	// Must be placed before any early returns to satisfy React hooks rules.
	useEffect(() => {
		if (!expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
			return;
		}
		if (hasAutoSubmittedRef.current) {
			return;
		}
		const userPrompt = remixModalMetadata?.userPrompt;
		if (!userPrompt) {
			return;
		}
		hasAutoSubmittedRef.current = true;
		onRemixFlowActiveChange(true);
		const autoSubmitType = remixModalMetadata?.dynamicUiType ?? 'custom';
		// When an upstream caller (currently the AI Image Generation media-insert tab)
		// supplies an `imageStyle` (e.g. "watercolors", "photorealistic"), pass it
		// through as the dynamicUiSubtype so the Remix backend can produce images in
		// the requested style. Falls back to "image" when no specific style was picked
		// so the backend always receives an explicit subtype signal.
		const autoSubmitSubType = remixModalMetadata?.imageStyle ?? 'image';
		void onOptionSelect({ type: autoSubmitType, subType: autoSubmitSubType, userPrompt });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [remixModalMetadata?.userPrompt]);

	useOnComplete();

	const submittedUpdateKeyRef = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!isViewMode && fg('platform_editor_ai_remix_toolbar')) {
			if (!isThinking || !isUpdate) {
				submittedUpdateKeyRef.current = undefined;
				return;
			}
			const { shouldSubmit, updateKey } = getUpdateRemixSubmissionDecision(
				historyEntryForUpdate,
				dynamicUiSubtypeForUpdate,
				submittedUpdateKeyRef.current,
			);
			if (shouldSubmit) {
				submittedUpdateKeyRef.current = updateKey;
				updateRemixRef.current?.();
			}
		}
	}, [isThinking, isUpdate, historyEntryForUpdate, dynamicUiSubtypeForUpdate, isViewMode]);

	if (!isViewMode && showLocationPicker && fg('platform_editor_ai_remix_in_editor_loading')) {
		return <LocationPickerPopup />;
	}

	// hide the thinking bar when creating the remix in editing mode
	const hideThinkingBar = !isViewMode && isThinking && !isUpdate;

	return (
		<RemixContainer
			hideThinkingBar={fg('platform_editor_ai_remix_toolbar') ? hideThinkingBar : undefined}
			showProgressBar={
				fg('platform_editor_maui_jira_updates') &&
				(!fg('platform_editor_ai_remix_in_editor_loading') || isViewMode)
			}
			isComplete={fg('platform_editor_ai_remix_in_editor_loading') ? pluginIsComplete : isComplete}
			isThinking={fg('platform_editor_ai_remix_in_editor_loading') ? isThinking : undefined}
			onCompleteAnimationFinished={
				fg('platform_editor_ai_remix_toolbar')
					? !hideThinkingBar
						? onCompleteAnimationFinished
						: undefined
					: onCompleteAnimationFinished
			}
			onClose={handleModalClose}
			onOptionSelect={onOptionSelect}
			onRemixFlowActiveChange={onRemixFlowActiveChange}
			isEphemeralViewVisible={productIntegration.isRemixPreviewVisible}
			hiddenOptions={hiddenOptions}
			contentType={productIntegration.contentType}
			autoSubmitUserPrompt={remixModalMetadata?.userPrompt ?? undefined}
			onStopThinking={onStopThinking}
			isRemixEntrance={
				expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
					? !remixModalMetadata?.userPrompt
					: true
			}
		/>
	);
};

export const RemixContentComponent = <
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
>(
	props: RemixContentComponentFullProps<TEditorApi>,
): React.JSX.Element | null => {
	// `startRemixThinking` closes the options modal (`isRemixModalOpen` false) while remix
	// is still active. We must keep rendering `RemixContainer` (thinking bar / ephemeral)
	// whenever the editor reports `isThinking`, not only when the modal is open.
	if (
		!(
			props.isModalOpen ||
			props.isThinking ||
			(props.showLocationPicker && fg('platform_editor_ai_remix_in_editor_loading'))
		)
	) {
		return null;
	}

	const inner = fg('platform_editor_ai_remix_in_editor_loading') ? (
		<FlagsProvider>
			<EditorRemixContextProvider
				isViewMode={props.isViewMode}
				clearThinkingDecoration={props.clearThinkingDecoration}
				replaceWhiteboardEmbed={props.replaceWhiteboardEmbed}
				entryPoint={props.entryPoint}
				smartCreationConfig={props.smartCreationConfig}
				smartCreationContext={props.smartCreationContext}
				setThinkingComplete={props.setThinkingComplete}
				finishRemixThinking={props.finishRemixThinking}
				onRemixThinkingCompleteAnimationFinished={props.onRemixThinkingCompleteAnimationFinished}
				showLocationPicker={props.showLocationPicker}
				setShowLocationPicker={props.setShowLocationPicker}
				addHistoryEntry={props.addHistoryEntry}
				addHistoryStackEntry={props.addHistoryStackEntry}
				onVariantUpdateComplete={props.onVariantUpdateComplete}
				onRemixInsertSuccess={props.productIntegration.onRemixInsertSuccess}
				isUpdate={props.isUpdate}
				historyEntryForUpdate={props.historyEntryForUpdate}
				dynamicUiSubtypeForUpdate={props.dynamicUiSubtypeForUpdate}
			>
				<RemixContentComponentInner
					clearThinkingDecoration={props.clearThinkingDecoration}
					replaceWhiteboardEmbed={props.replaceWhiteboardEmbed}
					handleModalClose={props.handleModalClose}
					isModalOpen={props.isModalOpen}
					isComplete={props.isComplete}
					selectedContext={props.selectedContext}
					editorApi={props.editorApi}
					setThinkingComplete={props.setThinkingComplete}
					finishRemixThinking={props.finishRemixThinking}
					onStartThinking={props.onStartThinking}
					onDismissThinkingUi={props.onDismissThinkingUi}
					onStopThinking={props.onStopThinking}
					onRegisterAbortRequest={props.onRegisterAbortRequest}
					remixModalMetadata={props.remixModalMetadata}
					remixSource={props.remixSource}
					isThinking={props.isThinking}
					productIntegration={props.productIntegration}
					productConfig={props.productConfig}
					hiddenOptions={props.hiddenOptions}
				/>
			</EditorRemixContextProvider>
		</FlagsProvider>
	) : (
		<RemixContentComponentInner
			clearThinkingDecoration={props.clearThinkingDecoration}
			replaceWhiteboardEmbed={props.replaceWhiteboardEmbed}
			handleModalClose={props.handleModalClose}
			isModalOpen={props.isModalOpen}
			isComplete={props.isComplete}
			selectedContext={props.selectedContext}
			editorApi={props.editorApi}
			setThinkingComplete={props.setThinkingComplete}
			finishRemixThinking={props.finishRemixThinking}
			onStartThinking={props.onStartThinking}
			onDismissThinkingUi={props.onDismissThinkingUi}
			onStopThinking={props.onStopThinking}
			onRegisterAbortRequest={props.onRegisterAbortRequest}
			remixModalMetadata={props.remixModalMetadata}
			remixSource={props.remixSource}
			isThinking={props.isThinking}
			productIntegration={props.productIntegration}
			productConfig={props.productConfig}
			hiddenOptions={props.hiddenOptions}
		/>
	);

	if (props.remixModalMetadata?.aiFeatureName) {
		return (
			<AIMateAnalyticsContext
				data={{ attributes: { aiFeatureName: props.remixModalMetadata.aiFeatureName } }}
			>
				{inner}
			</AIMateAnalyticsContext>
		);
	}

	return inner;
};
